export default defineNuxtPlugin((nuxtApp) => {
  const debugMode = useRuntimeConfig().public.GTAG_DEBUG;
  if (import.meta.dev && !debugMode) return;

  const measurementId = "G-30PHJECD8X";
  const { analytics: analyticsConsent } = useCookieConsent();
  const router = useRouter();
  const head = injectHead();
  const analyticsPageTitle = useAnalyticsPageTitleState();

  const gtagWindow = () =>
    window as unknown as {
      dataLayer?: unknown[];
      gtag?: (...args: unknown[]) => void;
    } & Record<string, unknown>;

  let lastTrackedFullPath: string | null = null;
  let navigationAwaitingPageView = false;
  let pageTitleApplied = false;

  const sendPageView = () => {
    const { gtag } = gtagWindow();
    const { fullPath } = router.currentRoute.value;
    if (!gtag || fullPath === lastTrackedFullPath) return;
    lastTrackedFullPath = fullPath;
    gtag("event", "page_view", { page_title: analyticsPageTitle.value ?? pageTitleFromDocumentTitle(document.title) });
  };

  const requestPageView = () => {
    if (pageTitleApplied) sendPageView();
    else navigationAwaitingPageView = true;
  };

  const flushNavigationPageView = () => {
    pageTitleApplied = true;
    if (!navigationAwaitingPageView) return;
    navigationAwaitingPageView = false;
    sendPageView();
  };

  const startTracking = () => {
    const w = gtagWindow();
    if (w.gtag) return;
    w.dataLayer = w.dataLayer ?? [];
    // gtag.js ignore une entrée du dataLayer qui n'est pas un objet `arguments` : ni fléchée, ni rest params.
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments);
    };
    w.gtag("js", new Date());
    w.gtag("config", measurementId, {
      send_page_view: false,
      ...(debugMode ? { debug_mode: true, traffic_type: "internal" } : {}),
    });
  };

  watch(
    analyticsConsent,
    (consented) => {
      gtagWindow()[`ga-disable-${measurementId}`] = !consented;
      if (!consented) {
        lastTrackedFullPath = null;
        return;
      }
      startTracking();
      requestPageView();
    },
    { immediate: true }
  );

  router.afterEach(() => {
    navigationAwaitingPageView = true;
    pageTitleApplied = false;
    analyticsPageTitle.value = null;
  });

  head.hooks.hook("dom:rendered", flushNavigationPageView);

  nuxtApp.hook("page:finish", () => {
    if (head.dirty) return;
    flushNavigationPageView();
  });

  // gtag.js pèse ~166 Kio à parser : on attend l'idle post-hydratation pour ne pas le mettre
  // en concurrence avec le LCP. Les `page_view` émis avant sont conservés par `dataLayer`.
  const idleReached = ref(false);
  onNuxtReady(() => {
    idleReached.value = true;
  });

  useHead({
    script: computed(() =>
      idleReached.value && analyticsConsent.value
        ? [{ key: measurementId, src: `https://www.googletagmanager.com/gtag/js?id=${measurementId}`, async: true }]
        : []
    ),
  });
});
